import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Link, useMatch } from 'react-router-dom';
import { assistantChat } from '../api';
import { cn } from './ui';
import { fmtDate } from '../format';
import type { AssistantMessage, AssistantSessionRef } from '../types';

/**
 * The recordings assistant: a chat panel over POST /api/assistant/chat.
 *
 * Everything clever happens on the server (routes/assistant.py): the model, the
 * tools, and the rule that the model never sees a clinical id. This component
 * keeps the conversation, says which recording is on screen, and shows the
 * sessions a reply drew on as links — those come with their clinical ids from
 * the server, after the model has answered.
 */

type Turn = AssistantMessage & { sessions?: AssistantSessionRef[]; error?: boolean };

// The server accepts at most 20 turns of at most 2000 characters each.
const HISTORY = 20;
const MAX_CHARS = 2000;

const WELCOME =
  'Ask me about recordings, for example "which recordings failed this week?", ' +
  '"explain this recording" or "compare this with the patient\'s earlier visits".';

function AssistantMark({ size = 26 }: { size?: number }) {
  return (
    <svg className="align-baseline" viewBox="0 0 32 32" width={size} height={size} aria-hidden="true">
      <path
        d="M7 20c2.4-7 4.6-9 6.6-9s2.2 7 4.2 7 3-4.5 6.2-4.5"
        stroke="var(--text)"
        strokeWidth="2.6"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function BrandMark({ size = 30 }: { size?: number }) {
  return (
    <svg className="align-baseline" viewBox="0 0 32 32" width={size} height={size} aria-hidden="true">
      <rect width="32" height="32" rx="8" fill="currentColor" />
      <path
        d="M7 20c2.4-7 4.6-9 6.6-9s2.2 7 4.2 7 3-4.5 6.2-4.5"
        stroke="var(--surface)"
        strokeWidth="2.4"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SessionLinks({ sessions }: { sessions: AssistantSessionRef[] }) {
  return (
    <ul className="mt-2 flex flex-col gap-1 border-t border-edge pt-2 text-[13px]">
      {sessions.map((s) => (
        <li key={s.sessionId}>
          <Link to={`/sessions/${encodeURIComponent(s.sessionId)}`} className="text-fg underline">
            {s.sessionId}
          </Link>
          <span className="text-fg-3">
            {s.clinicalId ? ` · ${s.clinicalId}` : ''} · {fmtDate(s.date)} · {s.status}
          </span>
        </li>
      ))}
    </ul>
  );
}

export default function Assistant() {
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [turns, setTurns] = useState<Turn[]>([]);
  const scroller = useRef<HTMLDivElement>(null);

  // On a session's page, "this recording" means that session.
  const sessionId = useMatch('/sessions/:id')?.params.id;

  useEffect(() => {
    document.body.classList.toggle('nxtq-chatbot-expanded', isOpen && isExpanded);
    return () => document.body.classList.remove('nxtq-chatbot-expanded');
  }, [isOpen, isExpanded]);

  useEffect(() => {
    scroller.current?.scrollTo({ top: scroller.current.scrollHeight });
  }, [turns, loading]);

  async function handleSend() {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');

    const asked: Turn[] = [...turns, { role: 'user', content: text }];
    setTurns(asked);
    setLoading(true);
    try {
      // Failed turns are shown, not sent: the model should not read our error text as its own words.
      const history = asked
        .filter((t) => !t.error)
        .slice(-HISTORY)
        .map(({ role, content }) => ({ role, content }));
      const answer = await assistantChat(history, sessionId);
      setTurns([...asked, { role: 'assistant', content: answer.reply, sessions: answer.sessions }]);
    } catch (error) {
      setTurns([
        ...asked,
        {
          role: 'assistant',
          error: true,
          content: error instanceof Error ? error.message : "I couldn't answer that right now.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') void handleSend();
  }

  const bubble = 'max-w-[85%] rounded-[12px] px-3.5 py-2.5 leading-[1.4] text-fg';

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setIsOpen((open) => {
            if (open) setIsExpanded(false);
            return !open;
          });
        }}
        aria-label={isOpen ? 'Close nxtCloud Assistant' : 'Open nxtCloud Assistant'}
        title="nxtCloud Assistant"
        className={cn(
          'fixed right-6 bottom-6 z-9999 grid size-[52px] cursor-pointer place-items-center rounded-[50%] border border-edge bg-accent p-0 text-center text-fg shadow-[0_7px_20px_rgba(14,26,43,0.16)]',
          isOpen && 'hidden'
        )}
      >
        <AssistantMark size={44} />
      </button>

      {isOpen && (
        <div
          className={cn(
            'fixed top-auto right-6 bottom-6 z-9999 flex h-[540px] w-[380px] flex-col overflow-hidden rounded-[16px] border border-edge bg-surface shadow-[0_10px_30px_rgba(14,26,43,0.16)] upto-900:w-[min(380px,calc(100vw-44px))]',
            isExpanded &&
              'from-901:top-0 from-901:right-0 from-901:bottom-0 from-901:h-screen from-901:rounded-none from-901:border-y-0 from-901:border-r-0 from-901:shadow-[-8px_0_24px_rgba(14,26,43,0.1)]'
          )}
        >
          <div className="flex items-center justify-between gap-2.5 bg-surface-3 px-4 py-3.5 font-semibold text-fg">
            <div className="flex items-center gap-[9px]">
              <span className="grid place-items-center text-accent">
                <BrandMark size={28} />
              </span>
              <span>nxtCloud Assistant</span>
            </div>
            <div className="flex items-center gap-1">
              {turns.length > 0 && (
                <button
                  type="button"
                  onClick={() => setTurns([])}
                  disabled={loading}
                  title="New Chat"
                  className="cursor-pointer border-none bg-transparent px-1 py-0.5 text-[13px] font-normal text-fg-2"
                >
                  New Chat
                </button>
              )}
              <button
                type="button"
                onClick={() => setIsExpanded((expanded) => !expanded)}
                aria-label={isExpanded ? 'Restore assistant' : 'Expand assistant'}
                title={isExpanded ? 'Restore assistant' : 'Expand assistant'}
                className="relative block cursor-pointer border-none bg-transparent px-1 py-0.5 text-center text-fg-2"
              >
                {isExpanded ? '⤡' : '⤢'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsExpanded(false);
                  setIsOpen(false);
                }}
                aria-label="Close assistant"
                className="relative block cursor-pointer border-none bg-transparent px-1 py-0.5 text-center"
              >
                ×
              </button>
            </div>
          </div>

          <div ref={scroller} className="flex flex-1 flex-col gap-3 overflow-y-auto p-[15px] whitespace-pre-line">
            <div className="flex justify-start">
              <div className={cn(bubble, 'bg-surface-3')}>
                {WELCOME}
                {sessionId && <div className="mt-1.5 text-[13px] text-fg-3">Looking at {sessionId}.</div>}
              </div>
            </div>

            {turns.map((turn, index) => (
              <div key={index} className={cn('flex', turn.role === 'user' ? 'justify-end' : 'justify-start')}>
                <div
                  className={cn(
                    bubble,
                    turn.role === 'user' ? 'bg-accent' : 'bg-surface-3',
                    turn.error && 'text-fg-2 italic'
                  )}
                >
                  {turn.content}
                  {turn.sessions && turn.sessions.length > 0 && <SessionLinks sessions={turn.sessions} />}
                </div>
              </div>
            ))}

            {loading && <div className="self-start text-[13px] text-fg-3">Thinking…</div>}
          </div>

          <div className="border-t border-edge bg-surface-3 p-3">
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                maxLength={MAX_CHARS}
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about recordings..."
                disabled={loading}
                className="flex-1 rounded-[8px] border p-2.5 outline-none dark:border-edge dark:bg-surface dark:text-fg"
              />
              <button
                type="button"
                onClick={() => void handleSend()}
                disabled={loading}
                className={cn(
                  'relative block rounded-[8px] border-none bg-accent px-4 py-2.5 text-center text-fg',
                  loading ? 'cursor-default opacity-60' : 'cursor-pointer'
                )}
              >
                Send
              </button>
            </div>
            <p className="mt-1.5 text-[11.5px] text-fg-3">
              Don't type patient names or clinical IDs. Answers come from a local AI model; check them against the
              results.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
